/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {CachedDataType, FilterOption} from '../types/types';
import {GroupsTreeFilterContainer} from './groupsTree/GroupsTreeFilterContainer';
import Filter, {AdditionalFeatures} from './Filter';
import {getMenuStyles} from './FilterHelpers';
import {GroupFilterProps} from './GroupFilter';
import {
  createSelectedData,
  createSelectedGroups,
  getSelectedCounts
} from './TreeFilterUtils';
import {FilterParamsProps} from './useFilterSubmit';

/**
 * Group Filter component with tree view implementation
 * ```typescript
 * import GroupsFilterTree from '@ion/dls/src/filters';
 *
 *  <GroupsFilterTree elementId={elementId}
 *   name={name}
 *   account={account}
 *   filterState={filterState}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onMenuClose={onMenuClose}
 *   primaryButtonDisabled={primaryButtonDisabled}
 *   secondaryButtonDisabled={secondaryButtonDisabled}
 *   showSelectedOptionsCount={false}
 *   isMobileView= {false}
 *   treeNodes={treeNodes}
 *   updateTreeNodes={updateTreeNodes}
 *   updateKeys={updateKeys}
 *   treeKeys={treeKeys}
 *   nodeKeys={nodeKeys}/>
 * ```
 */
export const GroupsFilterTree = forwardRef(({
  appliedSaveFilter,
  elementId,
  name,
  account,
  secondaryButtonDisabled,
  accountId,
  treeNodes,
  updateTreeNodes,
  updateKeys,
  onSelected,
  treeKeys,
  nodeKeys,
  onSubmit,
  onCancel,
  onMenuClose,
  onUpdateItemStored,
  cachedData,
  assetLoading,
  assetFilterState,
  deviceFilterState,
  scFilterProps,
  onMultiDropdownChecked,
  filterState = {},
  isMobileView = false,
  showSelectedOptionsCount = true
}: GroupFilterProps, ref) => {
  const [isFilterOpen, setisFilterOpen] = useState(false);
  const {
    hasSCIAccess,
    onCheckIncludeSCDevices,
    includeSCDevice
  } = scFilterProps || {};
  // on launch of asset groups filter, turn off includeSCDevices
  useEffect(() => {
    onCheckIncludeSCDevices?.(hasSCIAccess, elementId);
  }, []);

  const groupTreeFiltersRef = useRef(null);
  const {
    loading: isLoading,
    selected: selectedOptions,
    error
  } = filterState;

  async function handleSubmit({selections, options, loading, hasError}) {
    const checkedKeys = groupTreeFiltersRef?.current?.getCheckedKeys();
    // use checkedKeys with group only to find selectedGroups
    const selectedGroups = await createSelectedGroups({
      cachedData : cachedData,
      checkedKeys,
      groupTreeFiltersRef
    });
    // create selectedAssets array in proper format for the query string
    const createSelected = (type: CachedDataType) => {
      return cachedData ? createSelectedData(checkedKeys, cachedData, type) : [];
    };
    const selectedAssets = createSelected(CachedDataType.isAsset);
    const selectedDevices = createSelected(CachedDataType.isDevice);
    const filterParams: FilterParamsProps = {
      selectionsKey     : FilterOption.id,
      augmentSelections : selected => ({
        id   : Number(selected?.[FilterOption.id]),
        name : selected[FilterOption.name]
      }),
      hasError,
      loading,
      options,
      selectedAssets,
      selectedDevices : includeSCDevice ? selectedDevices : [],
      selectedGroups,
      selections      : selectedAssets // only used in Tracking filter
    };
    updateKeys(checkedKeys, 'SET_GROUP_FILTERS_CHECKED');
    onSubmit?.(name, filterParams);
  };

  useImperativeHandle(ref, () => ({
    addItem : groupTreeFiltersRef?.current?.addItem
  }));

  const getSelectedGroup = useCallback(async(checkedKeys) => {
    const selectedGroups = await createSelectedGroups({
      checkedKeys,
      cachedData,
      groupTreeFiltersRef
    });
    onMultiDropdownChecked?.(selectedGroups);
  }, [onMultiDropdownChecked, createSelectedGroups]);

  const {
    menuLeft,
    menuWidth,
    contentGridTemplateColumns,
    contentJustifyContent,
    contentGridColumnGap,
    contentGridRowGap
  } = getMenuStyles(isMobileView);

  const handleIncludeSCDevices = useCallback((e) => {
    onCheckIncludeSCDevices?.(e.target.checked);
  }, [onCheckIncludeSCDevices]);

  const SCDeviceFeatures: AdditionalFeatures = useMemo(() => ({
    permission : hasSCIAccess,
    state      : includeSCDevice,
    handler    : handleIncludeSCDevices,
    label      : translate('common:filters.includeSCDevices')
  }), [hasSCIAccess, includeSCDevice, handleIncludeSCDevices]);

  return (
    <Filter includeGroupsInSelectedOptionsCount
      optionType={translate('common:filters.groupsAssets')}
      disabled={false}
      onOpenClose={setisFilterOpen}
      optionPropId={FilterOption.id}
      optionPropLabel={FilterOption.name}
      loading={isLoading}
      selectedOptionIds={selectedOptions ? selectedOptions?.map(option => option?.id ?? option) : []}
      showSelectedOptionsCount={showSelectedOptionsCount}
      primaryButtonDisabled={false}
      secondaryButtonDisabled={secondaryButtonDisabled}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onMenuClose={onMenuClose}
      elementId={elementId}
      additionalFeatures={SCDeviceFeatures}
      menuLeft={menuLeft}
      menuWidth={menuWidth}
      contentGridTemplateColumns={contentGridTemplateColumns}
      contentJustifyContent={contentJustifyContent}
      contentGridColumnGap={contentGridColumnGap}
      contentGridRowGap={contentGridRowGap}
      accountId={accountId}
      isMobileView={isMobileView}
      selectedCount={getSelectedCounts(filterState, assetFilterState, deviceFilterState)}
      onFilterClose={groupTreeFiltersRef?.current?.onFilterClose}>
      <GroupsTreeFilterContainer ref={groupTreeFiltersRef}
        isFilterOpen={isFilterOpen}
        account={account}
        appliedSaveFilter={appliedSaveFilter}
        onUpdateItemStored={onUpdateItemStored}
        treeNodes={treeNodes}
        updateTreeNodes={updateTreeNodes}
        updateKeys={updateKeys}
        onSelected={onSelected}
        treeKeys={treeKeys}
        cachedData={cachedData}
        scFilterProps={scFilterProps}
        nodeKeys={nodeKeys}
        assetLoading={assetLoading}
        assetFilterState={assetFilterState}
        groupFilterState={filterState}
        deviceFilterState={deviceFilterState}
        isMobileView={isMobileView}
        selectedGroups={selectedOptions}
        onMultiDropdownChecked={getSelectedGroup} />
    </Filter>
  );
});
